import {
  ACCOUNT_MESSAGES,
  ADJUSTMENT_MESSAGES,
  MANUFACTURER_MESSAGES,
  MFG_MESSAGES,
  STOCK_MESSAGES,
  TRANSFER_MESSAGES,
  WAREHOUSE_MESSAGES,
  API_KEY_MESSAGES,
  NUMBER_SEQUENCE_MESSAGES,
  FA_MESSAGES,
  BILL_MESSAGES,
  BILLING_MESSAGES,
  PROJECT_MESSAGES,
  PROJECT_TASK_MESSAGES,
  SETTINGS_MESSAGES,
  TIMESHEET_MESSAGES,
  USER_MESSAGES,
  CATEGORY_MESSAGES,
  CREDIT_MESSAGES,
  DATA_TRANSFER_MESSAGES,
  EINVOICE_MESSAGES,
  FX_MESSAGES,
  GRN_MESSAGES,
  IMPORT_MESSAGES,
  ITEM_MESSAGES,
  JOURNAL_MESSAGES,
  ORDER_MESSAGES,
  BANKING_MESSAGES,
  PARTNER_MESSAGES,
  PAYMENT_MESSAGES,
  PERIOD_MESSAGES,
  PERM_MESSAGES,
  PO_MESSAGES,
  POS_MESSAGES,
  PURCHASE_RETURN_MESSAGES,
  QUOTATION_MESSAGES,
  RECEIPT_MESSAGES,
  REORDER_MESSAGES,
  SALES_MESSAGES,
  TENDER_MESSAGES,
  TIER_MESSAGES,
  UNIT_MESSAGES,
} from '@palaccount/contracts';
import type { TFunction } from 'i18next';

/**
 * GEN-151..GEN-155's world, at its seam: every message is a short fixed
 * sentence that says what to do next (GEN-151 — the contracts' *_MESSAGES
 * constants and the spec-wording tests that pin them); field messages sit by
 * their field (GEN-152 — FormMessage/inline errors), record-level failures
 * are the `role="alert"` red strip at the top (GEN-153), successes the green
 * `role="status"` strips (GEN-154), and non-blocking warnings the amber
 * strips that never auto-dismiss on their own (GEN-155).
 *
 * The slice's fixed server sentences mapped onto i18n keys, same policy as
 * SERVER_MESSAGE_KEYS in lib/session.ts: wording is part of the contract, so
 * the constants are imported rather than retyped, and an unknown sentence
 * passes through untranslated rather than being swallowed. The parameterised
 * sentences (taxIdTaken, owesOnSwitchOff, …) are functions in the contract
 * and cannot be exact-matched. Under D46 the API returns `key`+`params`
 * beside the composed `message` for CONVERTED members (the roster lives in
 * `apps/api/test/refusal-census.spec.ts`), and `localizeServerMessage`
 * prefers that structure when the caller hands it the cause; an unconverted
 * parameterised sentence still passes through in English — recorded, per
 * member, in that census rather than silently.
 *
 * ── "SERVER SENTENCES" WAS TOO NARROW, AND THE GAP WAS A DEFECT CLASS ──────
 *
 * A contract sentence does not have to travel over the wire to be shown. A
 * screen can import the constant and render it directly — a validation the
 * browser makes before it asks anything, or a note explaining what a control
 * costs. Those never become an `ApiError`, so `localizeServerCause` cannot
 * reach them and nothing about the seam applies; the sentence simply appears
 * in English in front of an Arabic reader. `DATA_TRANSFER_MESSAGES
 * .dateRequired` and `.importPostingTypes` are that shape.
 *
 * (That sentence used to say "the two entries at the foot of this map", and it
 * was false two commits later when the barcode pair was appended below them. A
 * comment that locates something by POSITION rots the next time anyone adds a
 * line; naming the thing costs the same and cannot.)
 *
 * So the map's subject is the CONTRACT's fixed sentences, whoever raises them,
 * and `contract-sentence-render.test.ts` is what keeps the two in step. Read
 * its header before adding an entry: it also records the two things this map
 * structurally cannot do, and one of them (an entry whose value is an i18n key
 * carrying an ICU argument) would render a sentence with a hole in it.
 */
const STATIC_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  [BANKING_MESSAGES.bouncedFlagClearReasonRequired]: 'banking.errFlagClearReason',
  [BANKING_MESSAGES.postingHeldTransition]: 'banking.errPostingHeld',
  [BANKING_MESSAGES.drawnOnRequired]: 'banking.errDrawnOnRequired',
  [BANKING_MESSAGES.drawnOnKind]: 'banking.errDrawnOnKind',
  [BANKING_MESSAGES.supplierOrPayeeRequired]: 'banking.errSupplierOrPayeeRequired',
  [BANKING_MESSAGES.returnReasonRequired]: 'banking.errReturnReasonRequired',
  [BANKING_MESSAGES.cancelReasonRequired]: 'banking.errCancelReasonRequired',
  [PARTNER_MESSAGES.nameRequired]: 'partners.errName',
  [PARTNER_MESSAGES.emailInvalid]: 'partners.errEmail',
  [PARTNER_MESSAGES.negativeCreditLimit]: 'partners.errNegativeCredit',
  // INV-F3 — the partner branches' two fixed sentences.
  [PARTNER_MESSAGES.branchNameTaken]: 'partners.errBranchNameTaken',
  [PARTNER_MESSAGES.branchNotOfPartner]: 'partners.errBranchNotOfPartner',
  [SALES_MESSAGES.noLines]: 'sales.errNoLines',
  [SALES_MESSAGES.zeroTotal]: 'sales.errZeroTotal',
  // Wave 4D — the cash invoice's two fixed sentences.
  [SALES_MESSAGES.cashAccountRequired]: 'sales.errCashAccountRequired',
  [SALES_MESSAGES.cashAccountKind]: 'sales.errCashAccountKind',
  [SALES_MESSAGES.negativePrice]: 'sales.errNegativePrice',
  [SALES_MESSAGES.discountRange]: 'sales.errDiscountRange',
  [SALES_MESSAGES.postedLocked]: 'sales.errPostedLocked',
  [SALES_MESSAGES.postedNoDelete]: 'sales.errPostedNoDelete',
  [SALES_MESSAGES.veryLarge]: 'sales.errVeryLarge',
  [SALES_MESSAGES.notADraft]: 'sales.errNotADraft',
  [SALES_MESSAGES.cancelOnlyPosted]: 'sales.errCancelOnlyPosted',
  [SALES_MESSAGES.cancelReasonRequired]: 'sales.errCancelReasonRequired',
  [SALES_MESSAGES.authorityAccepted]: 'sales.authorityAcceptedWarning',
  // FIN-08 + the SAL-04 gate. The two clearance sentences are DIFFERENT
  // strings in the spec — the strip's wording and the compliance screen's —
  // and both are kept, because a fixed sentence is part of the contract.
  [EINVOICE_MESSAGES.clearanceBlockedStrip]: 'sales.stripClearanceBlocked',
  [EINVOICE_MESSAGES.clearanceBlockedScreen]: 'einv.bannerBlocked',
  [EINVOICE_MESSAGES.clearedStrip]: 'sales.stripCleared',
  [EINVOICE_MESSAGES.clearedScreen]: 'einv.bannerCleared',
  [EINVOICE_MESSAGES.submitOnlyPending]: 'einv.errSubmitOnlyPending',
  [EINVOICE_MESSAGES.resubmitOnlyRejected]: 'einv.errResubmitOnlyRejected',
  [EINVOICE_MESSAGES.notPosted]: 'einv.errNotPosted',
  [EINVOICE_MESSAGES.contradictoryAnswer]: 'einv.errContradictory',
  [EINVOICE_MESSAGES.missingSellerTaxId]: 'einv.errMissingTaxId',
  [EINVOICE_MESSAGES.alreadySent]: 'einv.errAlreadySent',
  [RECEIPT_MESSAGES.overAllocated]: 'receipts.stripOver',
  [RECEIPT_MESSAGES.postedLocked]: 'receipts.lockedPosted',
  [RECEIPT_MESSAGES.cancelledLocked]: 'receipts.lockedCancelled',
  [RECEIPT_MESSAGES.amountRequired]: 'receipts.errAmountRequired',
  [RECEIPT_MESSAGES.customerRequired]: 'receipts.errCustomerRequired',
  // Wave 5C — the goods receipt without an order: its four fixed refusals,
  // and the mirror one on an order receipt.
  [GRN_MESSAGES.directNeedsPermission]: 'grn.errDirectNeedsPermission',
  [GRN_MESSAGES.directNeedsPartner]: 'grn.errDirectNeedsPartner',
  [GRN_MESSAGES.freeLineNeedsItem]: 'grn.errFreeLineNeedsItem',
  [GRN_MESSAGES.orderLineOnDirectReceipt]: 'grn.errOrderLineOnDirectReceipt',
  [GRN_MESSAGES.lineNeedsOrderLine]: 'grn.errLineNeedsOrderLine',
  [GRN_MESSAGES.landedCostAttached]: 'grn.errLandedCostAttached',
  [IMPORT_MESSAGES.receivedThroughGrn]: 'imports.errReceivedThroughGrn',
  [RECEIPT_MESSAGES.creditAccountRequired]: 'receipts.errCreditAccountRequired',
  [RECEIPT_MESSAGES.cashAccountsOnly]: 'receipts.errCashAccountsOnly',
  [RECEIPT_MESSAGES.creditAccountKind]: 'receipts.errCreditAccountKind',
  [RECEIPT_MESSAGES.allocationInvoiceInvalid]: 'receipts.errAllocationInvalid',
  [RECEIPT_MESSAGES.noRouteOutOfPosted]: 'receipts.errNoRouteOutOfPosted',
  [RECEIPT_MESSAGES.noOpenInvoices]: 'receipts.noOpenInvoices',
  // The reverse route's fixed sentences. The parameterised two (chequeRides,
  // reconciledLocked) pass through in English — the documented policy above.
  [RECEIPT_MESSAGES.reverseOnlyPosted]: 'receipts.errReverseOnlyPosted',
  [RECEIPT_MESSAGES.alreadyReversed]: 'receipts.errAlreadyReversed',
  [RECEIPT_MESSAGES.reverseReasonRequired]: 'receipts.errReverseReasonRequired',
  [PERM_MESSAGES.denied]: 'common.errDenied',
  // FIN-01 — chart maintenance (the parameterised sentences pass through).
  [ACCOUNT_MESSAGES.becomeHeadingWithPostings]: 'coa.errBecomeHeading',
  [ACCOUNT_MESSAGES.headingWithChildren]: 'coa.errHeadingChildren',
  [ACCOUNT_MESSAGES.deleteWithChildren]: 'coa.errDeleteChildren',
  [ACCOUNT_MESSAGES.parentLoop]: 'coa.errParentLoop',
  [ACCOUNT_MESSAGES.chartNotEmpty]: 'coa.errChartNotEmpty',
  // Q-FIN-01-3 — the period guard's two FIXED sentences. GEN-92's names the
  // period, so it is composed on the server and passes through in English
  // like every other parameterised sentence (see the note above); GEN-95's
  // and the reopen refusal are fixed and translate here.
  [PERIOD_MESSAGES.futureDated]: 'periods.errFutureDated',
  [PERIOD_MESSAGES.reopenForbidden]: 'periods.errReopenDenied',
  // FIN-02 — the journal workflow's fixed sentences.
  [JOURNAL_MESSAGES.unbalanced]: 'je.hintUnbalanced',
  [JOURNAL_MESSAGES.notApproved]: 'je.hintNotApproved',
  [JOURNAL_MESSAGES.postedLocked]: 'je.lockedPosted',
  [JOURNAL_MESSAGES.autoLocked]: 'je.errAutoLocked',
  [JOURNAL_MESSAGES.selfApproval]: 'je.errSelfApproval',
  [JOURNAL_MESSAGES.postedNoDelete]: 'je.errPostedNoDelete',
  [JOURNAL_MESSAGES.alreadyReversed]: 'je.errAlreadyReversed',
  [JOURNAL_MESSAGES.onlyPostedReverses]: 'je.errOnlyPostedReverses',
  [JOURNAL_MESSAGES.reverseReasonRequired]: 'je.errReverseReasonRequired',
  [JOURNAL_MESSAGES.notPending]: 'je.errNotPending',
  [JOURNAL_MESSAGES.empty]: 'je.errEmptyEntry',
  // The item master (MD-03/-04/-12/-14).
  [ITEM_MESSAGES.nameRequired]: 'items.errName',
  [ITEM_MESSAGES.negativePrice]: 'items.errNegativePrice',
  [ITEM_MESSAGES.negativeReorder]: 'items.errNegativeReorder',
  [ITEM_MESSAGES.factorZero]: 'items.errFactorZero',
  [ITEM_MESSAGES.kitInKit]: 'items.errKitInKit',
  [ITEM_MESSAGES.kitNeedsComponents]: 'items.errKitNeedsComponents',
  [UNIT_MESSAGES.precisionRange]: 'units.errPrecision',
  [UNIT_MESSAGES.notEmpty]: 'units.errNotEmpty',
  // S6 — the voucher tenders (RV-02…RV-06, PV-01).
  [TENDER_MESSAGES.atLeastOne]: 'tenders.errAtLeastOne',
  [TENDER_MESSAGES.notBoth]: 'tenders.errNotBoth',
  [TENDER_MESSAGES.amountRequired]: 'tenders.errAmountRequired',
  [TENDER_MESSAGES.chequeNoRequired]: 'tenders.errChequeNoRequired',
  [TENDER_MESSAGES.dueDateRequired]: 'tenders.errDueDateRequired',
  [TENDER_MESSAGES.accountRequired]: 'tenders.errAccountRequired',
  [TENDER_MESSAGES.endorsedChequeRequired]: 'tenders.errEndorsedRequired',
  [TENDER_MESSAGES.endorsedChequeNotInHand]: 'tenders.errEndorsedNotInHand',
  [TENDER_MESSAGES.endorsedChequeOnReceipt]: 'tenders.errEndorsedOnReceipt',
  [TIER_MESSAGES.nameRequired]: 'tiers.errName',
  [CATEGORY_MESSAGES.nameRequired]: 'cats.errName',
  // IT-S6 — the manufacturers master's three fixed sentences.
  [MANUFACTURER_MESSAGES.nameRequired]: 'mfr.errName',
  [MANUFACTURER_MESSAGES.nameTaken]: 'mfr.errNameTaken',
  [MANUFACTURER_MESSAGES.inactive]: 'mfr.errInactive',
  [CATEGORY_MESSAGES.tooDeep]: 'cats.errTooDeep',
  // The purchasing slice (PUR-01/PUR-03/FIN-04).
  // `PO_MESSAGES.approvalRequired()` had a DEFAULT-limit exact-match entry
  // here (`po.approvalBanner`) — retired under D46, dictionary key and all:
  // the refusal now travels with `key`+`params` naming the company's LIVE
  // limit, so the entry that only ever matched the default was the
  // brittleness the ruling names. `po-send-door-arabic.test.tsx` pins the
  // ICU behaviour at the send door in Arabic.
  [PO_MESSAGES.sentLocked]: 'po.sentNotice',
  [PO_MESSAGES.terminalLocked]: 'po.terminalNotice',
  [PO_MESSAGES.noLines]: 'po.errNoLines',
  [PO_MESSAGES.nothingToApprove]: 'po.errNothingToApprove',
  [PO_MESSAGES.notSendable]: 'po.errNotSendable',
  [PO_MESSAGES.sentNoDelete]: 'po.errSentNoDelete',
  // Wave 1C: the send door's expected-date requirement, same sentence as the
  // client's own validation (`po-rules.ts` canSend).
  [PO_MESSAGES.expectedDateRequired]: 'po.errExpectedDateRequired',
  // Wave 5E — the amend door's two fixed sentences, and PUR-06's new fixed
  // walls (cancel, the order date, direct imports, per-charge currency/bill).
  [PO_MESSAGES.notReopenable]: 'po.errNotReopenable',
  [PO_MESSAGES.reopenReasonRequired]: 'po.errReopenReasonRequired',
  [IMPORT_MESSAGES.cancelReasonRequired]: 'imports.errCancelReasonRequired',
  [IMPORT_MESSAGES.cancelledShipment]: 'imports.errCancelledShipment',
  [IMPORT_MESSAGES.orderDateYearFixed]: 'imports.errOrderDateYearFixed',
  [IMPORT_MESSAGES.orderDateLocked]: 'imports.errOrderDateLocked',
  [IMPORT_MESSAGES.directImportNeedsSupplierAndLines]: 'imports.errDirectNeedsSupplierAndLines',
  [IMPORT_MESSAGES.chargeBillNotPosted]: 'imports.errChargeBillNotPosted',
  [IMPORT_MESSAGES.chargeRateRequired]: 'imports.errChargeRateRequired',
  // Wave 5A — the reorder builder's fixed sentence (its parameterised one
  // rides structured, under reorder.errItemNotStocked).
  [REORDER_MESSAGES.noLines]: 'reorder.errNoLines',
  [BILL_MESSAGES.varianceBlocked]: 'bills.varianceBanner',
  [BILL_MESSAGES.supplierRefRequired]: 'bills.errSupplierRefRequired',
  [BILL_MESSAGES.dueBeforeDate]: 'bills.errDueBeforeDate',
  [BILL_MESSAGES.postedLocked]: 'bills.errPostedLocked',
  [BILL_MESSAGES.postedNoDelete]: 'bills.errPostedNoDelete',
  // BILL_MESSAGES.notADraft/noLines/zeroTotal are the SAME sentences as the
  // sales ones (§7.7's shared layout) — already mapped above.
  [BILL_MESSAGES.expenseAccountKind]: 'bills.errExpenseAccountKind',
  [BILL_MESSAGES.reasonRequired]: 'bills.errReasonRequired',
  [BILL_MESSAGES.nothingToAccept]: 'bills.errNothingToAccept',
  // Wave 1C: the four-eyes rule at the bill's posting door.
  [BILL_MESSAGES.varianceSelfApproval]: 'bills.errVarianceSelfApproval',
  [BILL_MESSAGES.approvalBanner]: 'bills.approvalBanner',
  [PAYMENT_MESSAGES.approvalRequired]: 'pay.approvalLine',
  [PAYMENT_MESSAGES.selfApproval]: 'pay.errSelfApproval',
  [PAYMENT_MESSAGES.postedLocked]: 'pay.lockedPosted',
  [PAYMENT_MESSAGES.cancelledLocked]: 'pay.lockedCancelled',
  [PAYMENT_MESSAGES.amountRequired]: 'pay.errAmountRequired',
  [PAYMENT_MESSAGES.supplierRequired]: 'pay.errSupplierRequired',
  [PAYMENT_MESSAGES.debitAccountRequired]: 'pay.errDebitAccountRequired',
  [PAYMENT_MESSAGES.cashAccountsOnly]: 'pay.errCashAccountsOnly',
  [PAYMENT_MESSAGES.debitAccountKind]: 'pay.errDebitAccountKind',
  [PAYMENT_MESSAGES.allocationBillInvalid]: 'pay.errAllocationInvalid',
  [PAYMENT_MESSAGES.overAllocated]: 'pay.stripOver',
  [PAYMENT_MESSAGES.noRouteOutOfPosted]: 'pay.errNoRouteOutOfPosted',
  [PAYMENT_MESSAGES.noOpenBills]: 'pay.noOpenBills',
  [PAYMENT_MESSAGES.approvalLocked]: 'pay.errApprovalLocked',
  [PAYMENT_MESSAGES.reverseOnlyPosted]: 'pay.errReverseOnlyPosted',
  [PAYMENT_MESSAGES.alreadyReversed]: 'pay.errAlreadyReversed',
  [PAYMENT_MESSAGES.reverseReasonRequired]: 'pay.errReverseReasonRequired',
  // Quotations (SAL-01).
  [QUOTATION_MESSAGES.noLines]: 'quotes.errNoLines',
  [QUOTATION_MESSAGES.validUntilBeforeIssue]: 'quotes.errValidUntil',
  [QUOTATION_MESSAGES.rejectedResend]: 'quotes.errRejectedResend',
  [QUOTATION_MESSAGES.sentLocked]: 'quotes.lockedNotice',
  [QUOTATION_MESSAGES.convertedLocked]: 'quotes.errConvertedLocked',
  [QUOTATION_MESSAGES.reviseFromDraft]: 'quotes.errReviseFromDraft',
  [QUOTATION_MESSAGES.notConvertible]: 'quotes.errNotConvertible',
  [QUOTATION_MESSAGES.alreadyConverted]: 'quotes.errAlreadyConverted',
  [QUOTATION_MESSAGES.notDraftDelete]: 'quotes.errNotDraftDelete',
  [QUOTATION_MESSAGES.historyNoDelete]: 'quotes.errHistoryNoDelete',
  // ERR-SAL-01-07 — the acceptance card's empty-pad refusal (BR-SAL-01-13).
  [QUOTATION_MESSAGES.signBeforeAccept]: 'quotes.errSignFirst',
  // BR-SAL-01-15 — the Sales rep write-guard's refusal.
  [QUOTATION_MESSAGES.salesRepNotOffered]: 'quotes.errSalesRepNotOffered',
  // Sales orders (SAL-02).
  [ORDER_MESSAGES.noLines]: 'orders.errNoLines',
  [ORDER_MESSAGES.zeroTotal]: 'orders.errZeroTotal',
  [ORDER_MESSAGES.notADraftConfirm]: 'orders.errNotADraftConfirm',
  [ORDER_MESSAGES.notDraftDelete]: 'orders.errNotDraftDelete',
  [ORDER_MESSAGES.confirmedLocked]: 'orders.errConfirmedLocked',
  [ORDER_MESSAGES.cancelledTerminal]: 'orders.errCancelledTerminal',
  [ORDER_MESSAGES.invoiceNeedsConfirmed]: 'orders.errInvoiceNeedsConfirmed',
  // Wave 4B — Amend after confirmation.
  [ORDER_MESSAGES.reopenNotConfirmed]: 'orders.errReopenNotConfirmed',
  [ORDER_MESSAGES.reopenReasonRequired]: 'orders.errReopenReasonRequired',
  // Credit notes (SAL-05).
  [CREDIT_MESSAGES.pickInvoice]: 'returns.errPickInvoice',
  [CREDIT_MESSAGES.otherNeedsNote]: 'returns.errOtherNeedsNote',
  [CREDIT_MESSAGES.pricesCopied]: 'returns.errPricesCopied',
  [CREDIT_MESSAGES.creditCap]: 'returns.errCreditCap',
  [CREDIT_MESSAGES.noLines]: 'returns.errNoLines',
  [CREDIT_MESSAGES.notADraft]: 'returns.errNotADraft',
  [CREDIT_MESSAGES.postedLocked]: 'returns.errPostedLocked',
  [CREDIT_MESSAGES.postedNoDelete]: 'returns.errPostedNoDelete',
  // Wave 4A — applying the credit to invoices; the three parameterised
  // siblings ride the D46 seam (CREDIT_REFUSALS).
  [CREDIT_MESSAGES.applyNotPosted]: 'returns.errApplyNotPosted',
  [CREDIT_MESSAGES.applyAlreadyRefunded]: 'returns.errApplyAlreadyRefunded',
  [CREDIT_MESSAGES.applyInvoiceNotOpen]: 'returns.errApplyInvoiceNotOpen',
  // PUR-04 — the debit note. The two parameterised sentences (the quantity
  // cap and the in-stock cap) carry live numbers, so they pass through in
  // English like every other server-composed sentence; the editor raises its
  // own translated copy of both BEFORE the round trip, which is the case a
  // user actually meets.
  [PURCHASE_RETURN_MESSAGES.pickBill]: 'preturn.errPickBill',
  [PURCHASE_RETURN_MESSAGES.pickSupplier]: 'preturn.errPickSupplier',
  [PURCHASE_RETURN_MESSAGES.pricesCopied]: 'preturn.errPricesCopied',
  [PURCHASE_RETURN_MESSAGES.debitCap]: 'preturn.errDebitCap',
  // `noLines` and `notADraft` are the SAME sentence the credit note uses and
  // are already mapped above — one string, one key.
  [PURCHASE_RETURN_MESSAGES.postedLocked]: 'preturn.errPostedLocked',
  [PURCHASE_RETURN_MESSAGES.postedNoDelete]: 'preturn.errPostedNoDelete',
  [PURCHASE_RETURN_MESSAGES.nothingToMove]: 'preturn.errNothingToMove',
  [PURCHASE_RETURN_MESSAGES.approvalBanner]: 'preturn.approvalBanner',
  // The admin wave (ADM-01/ADM-05/ADM-06 — PART 15).
  [QUOTATION_MESSAGES.internalApprovalRequired]: 'quotes.errInternalApproval',
  [SETTINGS_MESSAGES.cycleEndBeforeStart]: 'settings.errCycleOrder',
  [SETTINGS_MESSAGES.discountOver100]: 'settings.errDiscountOver100',
  [SETTINGS_MESSAGES.negativeNumber]: 'settings.errNegative',
  [SETTINGS_MESSAGES.ownerOnly]: 'settings.errOwnerOnly',
  [SETTINGS_MESSAGES.depositAccountIsTill]: 'settings.errDepositIsTill',
  [POS_MESSAGES.nothingToBank]: 'pos.errNothingToBank',
  [POS_MESSAGES.noDepositAccount]: 'pos.errNoDepositAccount',
  // `POS_MESSAGES.refundNeedsApproval()` had a DEFAULT-threshold exact-match
  // entry here (`pos.approvalLimitRefusal`) — retired under D46 with its
  // dictionary key: the refusal carries `key`+`params` naming the company's
  // LIVE threshold, which the entry could never do (a changed threshold
  // silently un-localized the refusal). `pos-refusal-doors-arabic.test.tsx`
  // pins the ICU behaviour at the door.
  [POS_MESSAGES.refundExceedsSold]: 'pos.errRefundExceedsSold',
  [POS_MESSAGES.noOpenShift]: 'pos.errNoOpenShift',
  [POS_MESSAGES.registerCodeTaken]: 'pos.errRegisterCodeTaken',
  [POS_MESSAGES.saleNotEditable]: 'pos.errSaleNotEditable',
  [POS_MESSAGES.shiftAlreadyOpen]: 'pos.errShiftAlreadyOpen',
  [POS_MESSAGES.noWarehouse]: 'pos.needWarehouse',
  [POS_MESSAGES.emptyCart]: 'pos.emptyCart',
  [POS_MESSAGES.tenderedShort]: 'pos.tenderedShort',
  [POS_MESSAGES.noProducts]: 'pos.noProducts',
  [POS_MESSAGES.saleNotFound]: 'pos.errSaleNotFound',
  [POS_MESSAGES.saleLineNotFound]: 'pos.errSaleLineNotFound',
  [SETTINGS_MESSAGES.notYours]: 'settings.errNotYours',
  [SETTINGS_MESSAGES.lockedSetting]: 'settings.errLocked',
  [SETTINGS_MESSAGES.unknownSetting]: 'settings.errUnknown',
  [SETTINGS_MESSAGES.badValue]: 'settings.errBadValue',
  [SETTINGS_MESSAGES.notLockable]: 'settings.errNotLockable',
  [USER_MESSAGES.emailInUse]: 'users.errEmailInUse',
  [USER_MESSAGES.ownAccount]: 'users.errOwnAccount',
  [USER_MESSAGES.ownerAccount]: 'users.errOwnerAccount',
  [USER_MESSAGES.transferTargetNotActive]: 'users.errTransferNotActive',
  [USER_MESSAGES.transferConfirmMismatch]: 'users.errTransferName',
  [USER_MESSAGES.adminUntouchable]: 'users.errAdminUntouchable',
  [USER_MESSAGES.inviteInvalid]: 'invite.errInvalid',
  [USER_MESSAGES.wrongStatus]: 'users.errWrongStatus',
  [PERM_MESSAGES.onlyOwnerAssignsAdmin]: 'roles.errOnlyOwnerAdmin',
  [PERM_MESSAGES.roleDeactivated]: 'roles.errDeactivated',
  [PERM_MESSAGES.ownRoleChange]: 'roles.errOwnRoleChange',
  [PERM_MESSAGES.grantNotHeld]: 'roles.errGrantNotHeld',
  [PERM_MESSAGES.viewRequired]: 'roles.errViewRequired',
  [PERM_MESSAGES.noViewLeft]: 'roles.errNoViewLeft',
  // The projects wave (PART 13 — PRJ-01..PRJ-04).
  [TIMESHEET_MESSAGES.locked]: 'prj.errLocked',
  [TIMESHEET_MESSAGES.selfApprove]: 'prj.errSelfApprove',
  [TIMESHEET_MESSAGES.nothingToSubmit]: 'prj.errNothingToSubmit',
  [TIMESHEET_MESSAGES.alreadySubmitted]: 'prj.errAlreadySubmitted',
  [TIMESHEET_MESSAGES.approved]: 'prj.approvedNotice',
  [BILLING_MESSAGES.nothingToBill]: 'prj.errNothingToBill',
  [BILLING_MESSAGES.nothingNewAtPct]: 'prj.errNothingNewAtPct',
  [BILLING_MESSAGES.progressWrongType]: 'prj.errProgressWrongType',
  [BILLING_MESSAGES.milestoneNotCompleted]: 'prj.errMilestoneNotCompleted',
  [BILLING_MESSAGES.milestoneAlreadyInvoiced]: 'prj.errMilestoneInvoiced',
  [PROJECT_MESSAGES.internalNonBillable]: 'prj.errInternalNonBillable',
  [PROJECT_TASK_MESSAGES.dependencyCycle]: 'prj.errDependencyCycle',
  [PROJECT_TASK_MESSAGES.notYourTask]: 'prj.errNotYourTask',
  // INV-08 §12's account mappings (the stock-accounts build).
  [ITEM_MESSAGES.notAnInventoryAccount]: 'items.errInventoryAccount',
  [SETTINGS_MESSAGES.notAnAccount]: 'settings.errNotAnAccount',
  // ADM-03 — and these two arrive by a route the rest of this file does not
  // describe. `dateRequired` IS also a server refusal
  // (`data-import.service.ts:394`), so it needs the entry for that path alone;
  // but BOTH of them are RAISED BY THE SCREEN, before any request is made, and
  // that is the case the whole map was written without. See
  // `contract-sentence-render.test.ts` for the class and what it can see.
  [DATA_TRANSFER_MESSAGES.dateRequired]: 'impexp.errDateRequired',
  [DATA_TRANSFER_MESSAGES.importPostingTypes]: 'impexp.postingTypesNote',
  // MD-03's barcode, for the barcode wave. Both are PLAIN STRINGS on purpose,
  // and that was a decision rather than an accident: `barcodeLength` is the
  // natural candidate for `(min, max) => …` and `barcodeTaken` would genuinely
  // read better naming the other item, and both were written fixed BECAUSE
  // this map is exact-match. A parameterised sentence could not be here at all.
  //
  // `barcodeTaken` reaches the strip only through `localizeServerMessage` at
  // `item-form-page.tsx`, so this entry is the whole fix. `barcodeLength` has a
  // SECOND path — the form's own zod schema names `items.errBarcodeLength`
  // directly — so the client-side bound is translated by the schema and this
  // entry covers only the server-400 route. One sentence, two mechanisms, both
  // covered; worth knowing before anyone "simplifies" either half.
  [ITEM_MESSAGES.barcodeTaken]: 'items.errBarcodeTaken',
  [ITEM_MESSAGES.barcodeLength]: 'items.errBarcodeLength',

  // The admin and finance census. Ten of these eleven sentences render at
  // exactly one site, so the entry is safe the moment its call site is wrapped.
  // `EINVOICE_MESSAGES.qrPlaceholderNote` is the eleventh, and it is the reason
  // `features/sales/compliance-strip.tsx` is wrapped in this same commit rather
  // than left to a sales wave: that sentence renders at TWO sites and an entry
  // here is global to both, so landing the entry alone would have moved the
  // sales site from `wrap-and-map` to `wrap` and turned the census detector red
  // naming a file the admin/finance change never touched. Entry and second
  // wrap are one atomic unit; splitting them turns main red in either order.
  [API_KEY_MESSAGES.copyWarning]: 'integrations.copyWarning',
  [API_KEY_MESSAGES.revokeWarning]: 'integrations.revokeBody',
  [API_KEY_MESSAGES.wiredAtDeployment]: 'integrations.wiredAtDeployment',
  [NUMBER_SEQUENCE_MESSAGES.badNextNumber]: 'numseq.errBadNextNumber',
  [NUMBER_SEQUENCE_MESSAGES.badWidth]: 'numseq.errBadWidth',
  [NUMBER_SEQUENCE_MESSAGES.locked]: 'numseq.lockedReason',
  [NUMBER_SEQUENCE_MESSAGES.prefixKeepsHistory]: 'numseq.prefixKeepsHistory',
  [FA_MESSAGES.categoryConfigDenied]: 'fa.errCategoryConfigDenied',
  [FA_MESSAGES.draftNotDisposable]: 'fa.errDraftNotDisposable',
  [FA_MESSAGES.disposedReadOnly]: 'fa.disposedReadOnly',
  [FA_MESSAGES.nothingEligible]: 'fa.nothingEligible',
  [EINVOICE_MESSAGES.qrPlaceholderNote]: 'einv.qrPlaceholderNote',

  // Manufacturing and inventory — twenty-four sites across nine screens, all
  // single-render, so none of them carries the two-site coupling that the
  // e-invoicing note above describes. Their Arabic is proved by
  // `mfg-inv-arabic-sentences.test.tsx`, which asserts in Arabic and states
  // why: every `en` value here is byte-identical to the contract sentence it
  // replaces, so an English assertion on any of them cannot tell a localised
  // render from a raw one. Deleting an entry below turns that file red.
  //
  // Three sentences from the same nine screens are NOT here and never can be
  // — `MFG_MESSAGES.lonelyWorkstation`, `MFG_MESSAGES.lateOrder` and
  // `WAREHOUSE_MESSAGES.zoneHasBins` are composed with live values, and this
  // map is exact-match. They are ICU keys addressed at their call sites.
  [MFG_MESSAGES.activatePromptTitle]: 'mfg.activatePromptTitle',
  [MFG_MESSAGES.activatePromptBody]: 'mfg.activatePromptBody',
  [MFG_MESSAGES.notUsedAnywhere]: 'mfg.notUsedAnywhere',
  [MFG_MESSAGES.immutabilityStrip]: 'mfg.immutabilityStrip',
  [MFG_MESSAGES.noMatchingRows]: 'mfg.noMatchingRows',
  [MFG_MESSAGES.correctionBanner]: 'mfg.correctionBanner',
  [MFG_MESSAGES.varianceHint]: 'mfg.varianceHint',
  [MFG_MESSAGES.usageFeedsMrp]: 'mfg.usageFeedsMrp',
  [MFG_MESSAGES.nothingWaiting]: 'mfg.nothingWaiting',
  [MFG_MESSAGES.noShortfalls]: 'mfg.noShortfalls',
  [MFG_MESSAGES.shortageBanner]: 'mfg.shortageBanner',
  [MFG_MESSAGES.shortageTitle]: 'mfg.shortageTitle',
  [MFG_MESSAGES.shortageBody]: 'mfg.shortageBody',
  [ADJUSTMENT_MESSAGES.zeroTotal]: 'adj.errZeroTotal',
  [ADJUSTMENT_MESSAGES.rejected]: 'adj.rejectedNotice',
  [ADJUSTMENT_MESSAGES.approvalRequired]: 'adj.approvalNotice',
  [ITEM_MESSAGES.belowCostNeedsManager]: 'items.errBelowCostNeedsManager',
  [STOCK_MESSAGES.reasonRequired]: 'mv.errReasonRequired',
  [TRANSFER_MESSAGES.sameLocation]: 'trf.errSameLocation',
  [TRANSFER_MESSAGES.noLines]: 'trf.errNoLines',
  [TRANSFER_MESSAGES.overAvailable]: 'trf.overAvailableBanner',
  [TRANSFER_MESSAGES.useDiscrepancy]: 'trf.useDiscrepancyHint',
  [TRANSFER_MESSAGES.discrepancyReason]: 'trf.errDiscrepancyReason',
  [WAREHOUSE_MESSAGES.codeAndName]: 'wh.errCodeAndName',
  /**
   * ERR-MD-11-01, and the FIRST FX sentence in this map. The other 27 in
   * `FX_MESSAGES` are not here because almost all of them are parameterised —
   * they name a code, a date or a rate — and a parameterised sentence cannot
   * be looked up by its text. This one is fixed, and it is raised by a screen
   * (the Add-currency box), which is the pair of conditions an entry needs.
   *
   * The Add-currency box also checks the shape itself before posting, so this
   * entry is the second line rather than the first: it catches the API client
   * and anything that posts a code the box never saw.
   */
  [FX_MESSAGES.codeShape]: 'fx.errCodeShape',
};

export type StaticMessageKeys = typeof STATIC_MESSAGE_KEYS;

/**
 * Exported for `contract-sentence-render.test.ts` ALONE, which needs to prove
 * that the map it reconstructs from this file's syntax is the map that exists
 * at runtime. Two fields that can disagree; a scanner with only its own reading
 * to go on is unfalsifiable.
 */
export const STATIC_MESSAGE_KEYS_FOR_TESTS: Readonly<Record<string, string>> = STATIC_MESSAGE_KEYS;

/**
 * D46's structured half, as the client reads it off a refusal body. `key`
 * names an ICU dictionary entry, `params` its named arguments; both are
 * optional on the wire and either may be absent on any given refusal. The
 * shapes are validated here rather than trusted, because the body is
 * whatever the server sent and this is the one place it enters typed code.
 */
export interface RefusalStructure {
  readonly key: string;
  readonly params: Readonly<Record<string, string | number>>;
}

function refusalStructureOf(body: unknown): RefusalStructure | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const { key, params } = body as { key?: unknown; params?: unknown };
  if (typeof key !== 'string' || key.length === 0) return undefined;
  const checked: Record<string, string | number> = {};
  if (params !== undefined) {
    if (typeof params !== 'object' || params === null) return undefined;
    for (const [name, value] of Object.entries(params)) {
      if (typeof value !== 'string' && typeof value !== 'number') return undefined;
      checked[name] = value;
    }
  }
  return { key, params: checked };
}

/**
 * D46 — a parameterised refusal prefers its `key`+`params` over the sentence.
 *
 * The third parameter is the CAUSE (or anything else — it is `unknown` so a
 * call site can pass what it holds without narrowing first). Only an
 * `ApiError` carrying a structured refusal changes anything; every other
 * value leaves the exact-match path exactly as it was, which is what keeps
 * the two-argument call sites and their behaviour untouched.
 *
 * The fallback is load-bearing, not decorative: `defaultValue` makes a key
 * the dictionaries no longer answer render the server's own English sentence
 * — the wire's compatibility promise — instead of the bare key.
 *
 * THE HOLE CHECK IS MEASURED, NOT ASSUMED. i18next-icu handed an argument the
 * pattern needs but the params lack does NOT throw — it returns the pattern
 * with the literal `{code}` still in it (probed directly against the
 * installed formatter). So "params dropped en route" would put a brace on a
 * refusal strip and every gate would stay green. A rendered result still
 * carrying an ICU-shaped hole (`{identifier` — argument names are ASCII
 * identifiers, which is also what the dictionary guards enforce) falls back
 * to the sentence instead. The deliberate cost: a param VALUE that itself
 * contains `{word` (a user could name an item that) makes a correct render
 * look like a hole and degrades to the English sentence — degraded but true,
 * which beats the inverse error. The try/catch covers the remaining rot: a
 * malformed pattern throws at ICU parse time, and a refusal strip must never
 * trade a refusal for a render error.
 */
const ICU_HOLE = /\{\s*[a-zA-Z_][a-zA-Z0-9_]*/;

/**
 * Distinguishes "the key rendered" from "the key is unanswered" — the two
 * need DIFFERENT fallbacks. An unanswered key falls through to the
 * exact-match map, because a default-argument member (the po.approvalBanner
 * shape) can be mid-retirement: wire already structured, map entry still the
 * one that answers. Falling straight to the sentence there would un-localize
 * a reader the map was already serving.
 */
const KEY_UNANSWERED = '__refusal-key-unanswered__';

export function localizeServerMessage(t: TFunction, message: string, cause?: unknown): string {
  const structured = cause instanceof ApiError ? cause.refusal : undefined;
  if (structured) {
    try {
      const rendered = t(structured.key, { ...structured.params, defaultValue: KEY_UNANSWERED });
      if (
        rendered !== KEY_UNANSWERED &&
        typeof rendered === 'string' &&
        rendered.length > 0 &&
        !ICU_HOLE.test(rendered)
      ) {
        return rendered;
      }
    } catch {
      /* a malformed pattern must never trade a refusal for a render error */
    }
  }
  const key = STATIC_MESSAGE_KEYS[message];
  return key ? t(key) : message;
}

/** A non-2xx contract response lifted into a throwable, status preserved. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * The whole refusal body, for the few contracts that return more than a
     * sentence. GEN-93 is the reason it exists: "Closing a period must be
     * refused while any document dated inside it is still Draft. The system
     * must LIST THOSE DOCUMENTS" — so the list travels with the refusal
     * rather than behind a second request the person has to think of.
     */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.refusal = refusalStructureOf(body);
  }

  /**
   * D46 — the structured half of a parameterised refusal, when the body
   * carried one. Read from the body ONCE here rather than at every consumer,
   * so `localizeServerCause` and a call site holding the error both see the
   * same validated value.
   */
  readonly refusal: RefusalStructure | undefined;
}

/** Extracts the contract's `{ message }` body (or falls back) and throws. */
export function throwApiError(status: number, body: unknown): never {
  const message =
    typeof body === 'object' && body !== null && 'message' in body
      ? String((body as { message: unknown }).message)
      : `Request failed (${status})`;
  throw new ApiError(status, message, body);
}

/**
 * `localizeServerMessage`'s sibling, and the ONLY difference that matters is
 * the parameter: this one takes the **cause**, not a string a call site has
 * already flattened out of it.
 *
 * ── WHY THIS CANNOT LIVE INSIDE `localizeServerMessage` ────────────────────
 *
 * `throwApiError` mints an `ApiError` only from a response that ARRIVED. A
 * `fetch` rejection is let through untouched by ts-rest, so `instanceof
 * ApiError` cleanly separates *the server considered this and refused* from
 * *nothing came back at all*. That distinction is present at every call site
 * and is destroyed by `cause instanceof Error ? cause.message : String(cause)`
 * one expression before `localizeServerMessage` is handed the result — by
 * which point a refusal and a dead connection are both just a string. So the
 * seam has to be a function that is given the cause; no amount of work inside
 * the string-taking one can recover what the caller already threw away.
 *
 * MENTION, NOT USE — the line above WRITES that shape in order to name it, so
 * every census of the remaining call sites counts this file as one of them and
 * is one too high. Measured: `git grep -l` for it returned 26 files at
 * `54214a8` and 27 with this comment present, the 27th being this line. The
 * words stay; deleting them to appease a grep is how the warning gets lost.
 * Subtract one, and count `apps/web/src/lib/**` out of the sweep.
 *
 * ── WHAT THE CALL SITE CAN ACTUALLY KNOW, WHICH IS LESS THAN "THE NETWORK" ─
 *
 * Not "the connection dropped" — a browser cannot see a cable, and a proxy
 * that accepted the request and never replied produces exactly this cause. It
 * knows only that the request did not come back, which is why the parameter is
 * `unansweredKey` and not `networkKey`, and why the sentence a screen supplies
 * should speak about the change rather than about the network. The admin wave
 * named its key `errSaveFailed` for the same reason.
 *
 * ── WHY THE SENTENCE IS A REQUIRED PARAMETER AND NOT A CONSTANT ────────────
 *
 * Because the right sentence differs by screen and the difference is load
 * bearing. `ERR-ADM-06-05` says *"That change was not saved. Check your
 * connection and try again."*; `ERR-ADM-08-02` (spec line 32962) is the same
 * event on the personalization screen and says *"That change was not saved to
 * your account. It applies on this device until you sign in again."* — because
 * there the change SURVIVES LOCALLY and the sentence says so. One shared
 * string would make the product less true on one of the two screens. What is
 * reusable here is the discriminator; the wording never is, so there is no
 * default and there must not be one.
 *
 * A screen that genuinely wants the server's raw sentence for the refusal case
 * wants `refusalMessage` below instead — this one localizes it.
 */
export function localizeServerCause(t: TFunction, cause: unknown, unansweredKey: string): string {
  if (cause instanceof ApiError) return localizeServerMessage(t, cause.message, cause);
  return t(unansweredKey);
}

/**
 * The SERVER's own sentence, for a screen to show unchanged.
 *
 * PART 9 words every refusal it has — "Only 260 Mounting Screws are on hand.
 * 260 reserved.", "One or more components have insufficient stock…" — and the
 * API answers with exactly those. A screen that paraphrased would be inventing
 * a second vocabulary for the same rule.
 */
export function refusalMessage(caught: unknown): string {
  if (caught instanceof ApiError) return caught.message;
  return caught instanceof Error ? caught.message : String(caught);
}
